import { TradingEntity, ProductSector, ProductRight, ValidationIssue } from 'api'
import { Locale, Translation } from 'locale'
import { intersection, isNil } from 'lodash-es'
import { createInvalidValueValidationIssue } from 'model'
import { TradingEntityProductAttributes, TradingEntityProductSpreadsheetParseResult } from './parseTradingEntityProductSpreadsheet'

export enum TradingEntityProductFields {
  ORGANISATION_SHORT_CODE = 'Organisation Short Code',
  TRADING_ENTITY_SHORT_NAME = 'Trading Entity Short Name',
  PRODUCT = 'Product',
  SECTOR = 'Sector',
  PERMISSION = 'Permission'
}

export interface TradingEntityProduct extends TradingEntityProductAttributes {
  idx: string
  tradingEntityId: string | null
  productRights: ProductRight[]
  validationIssues: ValidationIssue[]
}

export const validateTradingEntityProductParseResult = (
  parseResult: TradingEntityProductSpreadsheetParseResult,
  getTradingEntityByShortNameAndOrgShortCode: (orgShortCode: string, shortName: string) => TradingEntity | null,
  getProductSectorsByNameAndProductName: (productName: string, sectorName: string | null) => ProductSector[]
): TradingEntityProduct[] => {
  const { tradingEntityProducts } = parseResult

  const tradingEntityProductIds: string[] = []
  const duplicateIds: string[] = []
  return tradingEntityProducts.map(({ orgShortCode, shortName, productName, sectorName, permission, marketType }, idx) => {
    const validationIssues: ValidationIssue[] = []

    if (isNil(orgShortCode)) {
      validationIssues.push(
        createInvalidValueValidationIssue(
          TradingEntityProductFields.ORGANISATION_SHORT_CODE,
          Locale.translate(Translation.SC_VALUE_REQUIRED)
        )
      )
    }

    if (isNil(shortName)) {
      validationIssues.push(
        createInvalidValueValidationIssue(
          TradingEntityProductFields.TRADING_ENTITY_SHORT_NAME,
          Locale.translate(Translation.SC_VALUE_REQUIRED)
        )
      )
    }

    if (isNil(productName)) {
      validationIssues.push(
        createInvalidValueValidationIssue(TradingEntityProductFields.PRODUCT, Locale.translate(Translation.SC_VALUE_REQUIRED))
      )
    }

    if (isNil(permission)) {
      validationIssues.push(
        createInvalidValueValidationIssue(TradingEntityProductFields.PERMISSION, Locale.translate(Translation.SC_VALUE_REQUIRED))
      )
    }

    const tradingEntityId = getTradingEntityId(orgShortCode, shortName, getTradingEntityByShortNameAndOrgShortCode)
    if (!isNil(orgShortCode) && !isNil(shortName) && isNil(tradingEntityId)) {
      validationIssues.push(
        createInvalidValueValidationIssue(
          TradingEntityProductFields.ORGANISATION_SHORT_CODE,
          Locale.translate(Translation.SC_INVALID_TRADING_ENTITY, { shortName, orgShortCode })
        ),
        createInvalidValueValidationIssue(
          TradingEntityProductFields.TRADING_ENTITY_SHORT_NAME,
          Locale.translate(Translation.SC_INVALID_TRADING_ENTITY, { shortName, orgShortCode })
        )
      )
    }

    const productSectorIds = getProductSectorIds(productName, sectorName, getProductSectorsByNameAndProductName)
    if (!isNil(productName) && productSectorIds.length === 0) {
      validationIssues.push(
        createInvalidValueValidationIssue(
          TradingEntityProductFields.PRODUCT,
          Locale.translate(Translation.SC_INVALID_PRODUCT_SECTOR, { productName, sectorName: sectorName ?? '' })
        )
      )
      if (!isNil(sectorName)) {
        validationIssues.push(
          createInvalidValueValidationIssue(
            TradingEntityProductFields.SECTOR,
            Locale.translate(Translation.SC_INVALID_PRODUCT_SECTOR, { productName, sectorName: sectorName ?? '' })
          )
        )
      }
    }

    if (isNil(tradingEntityId) || productSectorIds.length === 0 || isNil(permission)) {
      return {
        idx: idx.toString(),
        tradingEntityId: null,
        productRights: [],
        orgShortCode,
        shortName,
        productName,
        sectorName,
        permission,
        validationIssues,
        marketType
      }
    }

    const rowTradingEntityProductIds = productSectorIds.map(productSectorId => `${tradingEntityId}-${productSectorId}`)
    rowTradingEntityProductIds.forEach(id => {
      if (tradingEntityProductIds.includes(id)) {
        duplicateIds.push(id)
      } else {
        tradingEntityProductIds.push(id)
      }
    })

    const hasDuplicates = intersection(duplicateIds, rowTradingEntityProductIds).length > 0

    if (hasDuplicates) {
      const errorMsg = Locale.translate(Translation.SC_DUPLICATE_TRADING_ENTITY_PRODUCT, {
        orgShortCode,
        shortName,
        productName,
        sectorName: sectorName ?? ''
      })
      validationIssues.push(
        createInvalidValueValidationIssue(TradingEntityProductFields.ORGANISATION_SHORT_CODE, errorMsg),
        createInvalidValueValidationIssue(TradingEntityProductFields.TRADING_ENTITY_SHORT_NAME, errorMsg),
        createInvalidValueValidationIssue(TradingEntityProductFields.PRODUCT, errorMsg)
      )
      if (!isNil(sectorName)) {
        validationIssues.push(createInvalidValueValidationIssue(TradingEntityProductFields.SECTOR, errorMsg))
      }
    }

    return {
      idx: idx.toString(),
      tradingEntityId: hasDuplicates ? null : tradingEntityId,
      productRights: hasDuplicates ? [] : productSectorIds.map(productSectorId => ({ productSectorId, permission, marketType })),
      orgShortCode,
      shortName,
      productName,
      sectorName,
      permission,
      validationIssues,
      marketType
    }
  })
}

function getTradingEntityId(
  orgShortCode: string | null,
  shortName: string | null,
  getTradingEntityByShortNameAndOrgShortCode: (orgShortCode: string, shortName: string) => TradingEntity | null
): string | null {
  if (isNil(orgShortCode) || isNil(shortName)) {
    return null
  }
  return getTradingEntityByShortNameAndOrgShortCode(orgShortCode, shortName)?.id ?? null
}

function getProductSectorIds(
  productName: string | null,
  sectorName: string | null,
  getProductSectorsByNameAndProductName: (productName: string, sectorName: string | null) => ProductSector[]
): string[] {
  if (isNil(productName)) {
    return []
  }
  return getProductSectorsByNameAndProductName(productName, sectorName).map(sector => sector.id)
}
